import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSave, FaGamepad, FaToggleOn, FaToggleOff, FaArrowLeft, FaPlay } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import RetroSnakeGame from '../components/RetroSnakeGame';

const apiFetch = async (url, options = {}) => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  return response.json();
};

const getAuthHeaders = async (getAccessTokenSilently) => {
  try {
    const token = await getAccessTokenSilently();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  } catch (error) {
    console.error('[RETRO-GAMEMANAGEMENT] Error getting auth token:', error);
    throw error;
  }
};

const GameManagement = ({ onBack, onPlayGame }) => {
  console.log('[RETRO-GAMEMANAGEMENT] 🎮 Component mounted');
  const { getAccessTokenSilently } = useAuth0();
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSnakeGameOpen, setIsSnakeGameOpen] = useState(false);

  useEffect(() => {
    console.log('[RETRO-GAMEMANAGEMENT] 🔄 useEffect triggered, calling fetchConfig');
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    console.log('[RETRO-GAMEMANAGEMENT] 📡 Starting fetchConfig');
    try {
      setLoading(true);
      console.log('[RETRO-GAMEMANAGEMENT] 🔄 About to call apiFetch with /api/gameconfig');
      const data = await apiFetch('/api/gameconfig');
      console.log('[RETRO-GAMEMANAGEMENT] ✅ Received data:', data);
      
      // Ensure Snake game is included
      const configWithSnake = ensureSnakeGame(data);
      setConfig(configWithSnake);
      setOriginalConfig(JSON.parse(JSON.stringify(configWithSnake))); // Deep clone for comparison
      console.log('[RETRO-GAMEMANAGEMENT] 🎯 Config state updated with Snake game');
    } catch (error) {
      console.error('[RETRO-GAMEMANAGEMENT] ❌ Error fetching game config:', error);
      toast.error('Failed to load game configuration');
    } finally {
      console.log('[RETRO-GAMEMANAGEMENT] 🏁 Setting loading to false');
      setLoading(false);
    }
  };

  // Check if configuration has been modified
  const hasChanges = () => {
    return JSON.stringify(config) !== JSON.stringify(originalConfig);
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      const authHeaders = await getAuthHeaders(getAccessTokenSilently);
      await apiFetch('/api/gameconfig', {
        method: 'PUT',
        body: JSON.stringify(config),
        headers: authHeaders,
      });
      toast.success('Game configuration saved successfully!');
      setOriginalConfig(JSON.parse(JSON.stringify(config))); // Update original after save
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleGame = (gameId) => {
    setConfig(prev => ({
      ...prev,
      games: prev.games.map(game =>
        game.id === gameId ? { ...game, isActive: !game.isActive } : game
      )
    }));
  };

  const handlePlayGame = (game) => {
    if (game.id === 'snake') {
      setIsSnakeGameOpen(true);
    } else {
      onPlayGame?.(game);
    }
  };

  // Ensure Snake game is in the config
  const ensureSnakeGame = (gameConfig) => {
    if (!gameConfig || !gameConfig.games) return gameConfig;
    
    const hasSnakeGame = gameConfig.games.some(game => game.id === 'snake');
    if (!hasSnakeGame) {
      return {
        ...gameConfig,
        games: [
          ...gameConfig.games,
          {
            id: 'snake',
            title: 'Retro Snake 🐍',
            description: 'Classic Snake game with retro styling. Use WASD or arrow keys to move and eat apples!',
            isActive: true,
            category: 'arcade',
            difficulty: 'medium'
          }
        ]
      };
    }
    return gameConfig;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-[#00FF9F]/30 border-t-[#00FF9F] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-12">
        <p className="text-black/60 font-['VT323'] text-lg">Failed to load configuration</p>
      </div>
    );
  }

  return (
    <div className="max-w-[920px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <motion.button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaArrowLeft className="w-4 h-4" />
            Back to Admin
          </motion.button>
          <div>
            <h1 className="text-2xl font-['VT323'] text-black/60 flex items-center gap-2">
              <FaGamepad className="w-6 h-6" />
              Retro Arcade
            </h1>
            <p className="text-sm font-['VT323'] text-black/40">
              Toggle which games are active and play them
            </p>
          </div>
        </div>

        {hasChanges() && (
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={saveConfig}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#00FF9F]/20 hover:bg-[#00FF9F]/30 text-[#008F59] rounded-lg font-['VT323'] text-[18px] transition-colors disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaSave className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </motion.button>
        )}
      </div>

      {/* Game List */}
      <div className="space-y-4">
        {config.games.map((game, index) => (
          <motion.div
            key={game.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08]"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-['VT323'] text-xl text-black/70">
                  {game.title}
                </h3>
                <p className="text-sm font-['VT323'] text-black/50 mt-1">
                  {game.description}
                </p>
                <div className="mt-2">
                  <span className="text-xs font-['VT323'] text-black/40">
                    Game ID: {game.id}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <motion.button
                  onClick={() => toggleGame(game.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-['VT323'] text-[18px] transition-colors ${
                    game.isActive 
                      ? 'bg-[#00FF9F]/20 text-[#008F59] hover:bg-[#00FF9F]/30' 
                      : 'bg-black/[0.03] text-black/40 hover:bg-black/[0.05]'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {game.isActive ? (
                    <>
                      <FaToggleOn className="w-5 h-5" />
                      Active
                    </>
                  ) : (
                    <>
                      <FaToggleOff className="w-5 h-5" />
                      Inactive
                    </>
                  )}
                </motion.button>

                <motion.button
                  onClick={() => handlePlayGame(game)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-600 hover:bg-blue-500/30 rounded-lg font-['VT323'] text-[18px] transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FaPlay className="w-4 h-4" />
                  Play
                </motion.button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Summary */}
      <div className="mt-8 bg-black/[0.03] rounded-xl p-4">
        <div className="flex items-center justify-between">
          <span className="font-['VT323'] text-[18px] text-black/60">
            Active Games: {config.games.filter(g => g.isActive).length} / {config.games.length}
          </span>
          {config.lastUpdated && (
            <span className="text-sm font-['VT323'] text-black/40">
              Last updated: {new Date(config.lastUpdated).toLocaleDateString()} by {config.updatedBy || 'System'}
            </span>
          )}
        </div>
      </div>

      {/* Snake Game Modal */}
      <AnimatePresence>
        {isSnakeGameOpen && (
          <RetroSnakeGame
            isOpen={isSnakeGameOpen}
            onClose={() => setIsSnakeGameOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default GameManagement; 